// The bookmarklet is built from this template by stripping indentation and
// joining all lines, so the whole script ends up on a single line. Do not use
// `//` comments inside the template, and write `\\n` (not `\n`) inside string
// literals that need a newline at runtime.
//
// The token is passed as the very first thing in the URL
// (`Promise.resolve("<token>").then(main)`), so an agent whose token rotated can
// open the bookmark's edit dialog and swap it right at the start. When the
// internal API rejects the token, the script asks for a fresh one, rebuilds its
// own URL with it (`main.toString()`), shows it in an in-page overlay to copy,
// and retries. Never hand the URL over through `prompt()`: Chrome elides a
// prompt's default value to 2000 chars (middle replaced by `...`), and the
// bookmarklet is longer than that.
export default function loginBookmarklet(customerToken: string) {
  return `javascript:void(
    Promise.resolve(${JSON.stringify(customerToken)}).then(
      async function main(TOKEN) {
        function getInternalEndpoint() {
          const internalMappings = {
            ".admin.staging-datocms.com":"internal.staging-datocms.com",
            "dashboard.staging-datocms.com":"internal.staging-datocms.com",
            ".admin-2.staging-datocms.com":"internal-2.staging-datocms.com",
            "dashboard-2.staging-datocms.com":"internal-2.staging-datocms.com",
            ".admin-3.staging-datocms.com":"internal-3.staging-datocms.com",
            "dashboard-3.staging-datocms.com":"internal-3.staging-datocms.com",
            ".admin-next.datocms.com":"internal.datocms.com",
            "dashboard-next.datocms.com":"internal.datocms.com"
          };

          for (const key of Object.keys(internalMappings)) {
            if (document.location.hostname.endsWith(key)) {
              return internalMappings[key];
            }
          }

          return 'internal.datocms.com';
        }

        const PRODUCTION_WARNING = "I'm logging you in as the PRODUCTION user. Just look around, do not touch any data!";

        const endpoint = getInternalEndpoint();

        const emailInput = document.querySelector('input[type=email]');
        const email = emailInput && emailInput.value;

        const params = new URLSearchParams();
        params.set('url', document.location);

        const storedDomain = window.localStorage.getItem('domain');
        const localhostMatch = document.location.hostname.match(/^(.+)\\.admin\\.(?:lvh\\.me|localhost)$/);
        const adminNextMatch = document.location.hostname.match(/^(.+)\\.admin-next\\.datocms\\.com$/);

        if (storedDomain) {
          if (!confirm(PRODUCTION_WARNING)) {
            return;
          }
          params.set('domain', storedDomain);
        } else if (localhostMatch) {
          if (!confirm(PRODUCTION_WARNING)) {
            return;
          }
          params.set('domain', localhostMatch[1] + '.admin.datocms.com');
        } else if (document.location.host === 'localhost:8080') {
          if (!confirm(PRODUCTION_WARNING)) {
            return;
          }
          params.set('url', 'https://dashboard.datocms.com/');
        } else if (adminNextMatch) {
          params.set('domain', adminNextMatch[1] + '.admin.datocms.com');
        } else if (document.location.hostname === 'dashboard-next.datocms.com') {
          params.set('url', 'https://dashboard.datocms.com/');
        }

        if (email) {
          params.set('email', email);
        }

        const loginUrlEndpoint = 'https://' + endpoint + '/customers/login-url?' + params.toString();

        const fetchLoginUrl = (token) => fetch(
          loginUrlEndpoint,
          { headers: { Authorization: 'Bearer ' + token } },
        );

        let response = await fetchLoginUrl(TOKEN);

        if (response.status === 401 || response.status === 403) {
          const newToken = prompt(
            'The internal API token in this bookmarklet is missing or no longer valid.\\n\\n' +
            'Option A: paste the current token here (Bitwarden: CUSTOMER_INSPECT_TOKEN_LVL2). ' +
            'The bookmarklet will rebuild itself with the new token and hand you the updated URL to save.\\n\\n' +
            'Option B: press Cancel and either\\n' +
            '- edit the bookmark URL by hand, replacing the token (the first quoted string, right at the start) with the one from Bitwarden, or\\n' +
            '- open the Front app plugin and drag a fresh Autologin bookmarklet to your bookmarks bar.'
          );

          if (!newToken) {
            return;
          }

          const updatedBookmarklet = 'javascript:void(Promise.resolve(' + JSON.stringify(newToken.trim()) + ').then(' + main.toString() + '))';

          await new Promise((resolve) => {
            const overlay = document.createElement('div');
            Object.assign(overlay.style, { position: 'fixed', inset: '0', zIndex: '2147483647', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' });
            const box = document.createElement('div');
            Object.assign(box.style, { background: '#fff', color: '#000', padding: '16px', borderRadius: '8px', width: 'min(720px, 90vw)' });
            const label = document.createElement('p');
            label.textContent = 'Updated bookmarklet: copy it, then edit the bookmark and replace its whole URL with it.';
            const textarea = document.createElement('textarea');
            textarea.value = updatedBookmarklet;
            textarea.readOnly = true;
            Object.assign(textarea.style, { width: '100%', height: '200px', fontFamily: 'monospace', fontSize: '11px', boxSizing: 'border-box' });
            const copyButton = document.createElement('button');
            copyButton.textContent = 'Copy';
            copyButton.onclick = async () => {
              textarea.select();
              try {
                await navigator.clipboard.writeText(updatedBookmarklet);
                copyButton.textContent = 'Copied!';
              } catch (e) {
                copyButton.textContent = document.execCommand('copy') ? 'Copied!' : 'Copy failed: press Cmd/Ctrl+C';
              }
            };
            const doneButton = document.createElement('button');
            doneButton.textContent = 'Done, log me in';
            doneButton.style.marginLeft = '8px';
            doneButton.onclick = () => {
              overlay.remove();
              resolve();
            };
            box.append(label, textarea, copyButton, doneButton);
            overlay.append(box);
            document.body.append(overlay);
            textarea.select();
          });

          response = await fetchLoginUrl(newToken.trim());
        }

        if (response.status !== 200) {
          alert('Fail! (' + response.status + ')');
          return;
        }

        const data = await response.json();

        const url = new URL(data.url);
        try {
          document.location = url.pathname + url.search + '&path=' + encodeURIComponent(history.state.state.nextPathname.pathname);
        } catch(e) {
          document.location = url.pathname + url.search;
        }
      }
    )
  )`
    .split(/\n/)
    .map((l) => l.replace(/^ */, ''))
    .join('');
}
